import { XCircle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

interface ErrorMessageProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}

export function ErrorMessage({
  title,
  message,
  onRetry,
  retryLabel = "Retry",
  className,
}: ErrorMessageProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4 rounded-lg border border-error/20 bg-error/5 p-6 text-center",
        className
      )}
      role="alert"
      aria-live="assertive"
    >
      <XCircle
        className="h-12 w-12 text-error"
        aria-hidden="true"
      />

      <div className="space-y-1">
        {title && (
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        )}
        <p className="text-sm text-foreground-secondary">{message}</p>
      </div>

      {onRetry && (
        <button
          onClick={onRetry}
          className={cn(
            "inline-flex items-center gap-2 rounded px-4 py-2 text-sm font-medium",
            "bg-error text-white hover:bg-red-600",
            "focus:outline-none focus:ring-2 focus:ring-error/40 focus:ring-offset-2 focus:ring-offset-background",
            "transition-colors duration-base"
          )}
          type="button"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          {retryLabel}
        </button>
      )}
    </div>
  );
}

export function InlineError({
  message,
  className,
}: {
  message: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 text-sm text-error",
        className
      )}
      role="alert"
    >
      <XCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}
